/**
 * API Client for SurrealEstateSearch backend
 */

const API_BASE = '/api';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export type AIProvider = 'openai' | 'grok';

// ============================================
// Chat API
// ============================================

export async function sendChatMessage(
  messages: ChatMessage[],
  provider?: AIProvider
): Promise<{ message: string; isComplete: boolean; provider: AIProvider }> {
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, provider }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Chat failed');
  return data.data;
}

export async function extractProfile(
  messages: ChatMessage[],
  provider?: AIProvider
): Promise<{ profile: any; provider: AIProvider }> {
  const res = await fetch(`${API_BASE}/chat/extract-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, provider }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Extraction failed');
  return data.data;
}

export async function getProviders(): Promise<{ providers: AIProvider[]; default: AIProvider | null }> {
  const res = await fetch(`${API_BASE}/chat/providers`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Failed to get providers');
  return data.data;
}

// ============================================
// Search API
// ============================================

export async function searchListings(profile: any): Promise<{
  topMatches: any[];
  nearMisses: any[];
  stats: { total: number; matched: number; nearMiss: number; excluded: number };
}> {
  const res = await fetch(`${API_BASE}/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Search failed');
  return data.data;
}

export async function getListingDetail(id: number): Promise<any> {
  const res = await fetch(`${API_BASE}/listings/${id}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Listing not found');
  return data.data;
}

// ============================================
// Layers API
// ============================================

export async function getLayer(name: string): Promise<any> {
  const res = await fetch(`${API_BASE}/layers/${name}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Layer not found');
  return data.data;
}

// ============================================
// Health
// ============================================

export async function healthCheck(): Promise<any> {
  const res = await fetch(`${API_BASE}/health`);
  return res.json();
}
